import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Avatar, Box, CircularProgress, Dialog, IconButton, Slider, Stack, Typography, useMediaQuery } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded'
import PauseRoundedIcon from '@mui/icons-material/PauseRounded'
import VolumeUpRoundedIcon from '@mui/icons-material/VolumeUpRounded'
import VolumeOffRoundedIcon from '@mui/icons-material/VolumeOffRounded'
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded'
import { useTranslation } from 'react-i18next'
import { fmtTime, FA_COLOR, STAGE_BG, vectorAt, type EmotionKey } from '@/components/researcher/liveDemo/constants'
import { createRelativeFormatter } from '@/components/researcher/studyRows'
import type { EmotionRespondent, EmotionSeries, EmotionStimulus } from '@/services/emotionAnalysisService'
import StimulusStage from './StimulusStage'
import FaceStage from './FaceStage'
import GazeDot from './GazeDot'
import GazeHeatLayer, { type HeatField } from './GazeHeatLayer'
import EmotionRadar from './EmotionRadar'
import { radarShares } from './emotionStats'
import { STAGE_ASPECT } from './stageFrame'
import type { PrototypeReplayData } from './PrototypeReplay'

const NAVY = '#0F1024'
const GLASS = {
  bgcolor: 'rgba(255,255,255,.06)',
  border: '1px solid rgba(255,255,255,.14)',
  backdropFilter: 'blur(14px)',
}

/**
 * Largest box of ratio `aspect` that fits its parent, centred. Container query
 * units do the fitting, so the stage inside keeps its own 16:9 (or the face
 * recording's) shape and its overlays (gaze dot, face rectangle) stay mapped
 * onto the media rather than onto a letterbox.
 */
function FitBox({ aspect, children }: { aspect: number; children: React.ReactNode }) {
  return (
    <Box sx={{ position: 'relative', width: '100%', height: '100%', containerType: 'size', display: 'grid', placeItems: 'center' }}>
      <Box sx={{ width: `min(100cqw, calc(100cqh * ${aspect}))` }}>{children}</Box>
    </Box>
  )
}

/**
 * Full-screen replay of one respondent (Z-121): their face and the stimulus
 * they watched, a transparent emotion radar for the current moment, and the
 * whole respondent list to jump between people without leaving the view.
 *
 * Desktop: face | stimulus (radar in its bottom-right corner) | scrollable
 * respondent rail. Phone (9:16): stimulus on top, radar and face side by side
 * under it, a horizontally scrollable respondent strip above the transport.
 *
 * Owns no playback state: the clock, the stimulus <video> and the data all
 * stay in EmotionAnalysisPanel, so closing the view lands back on the same
 * moment and respondent.
 */
export default function RespondentFullscreen({
  open,
  onClose,
  stimulus,
  stimulusLabel,
  stageCurrentMs,
  playing,
  muted,
  onTick,
  onEnded,
  onVideoFailed,
  onTogglePlay,
  onToggleMute,
  prototypeReplay,
  gazePoint,
  heatField = null,
  faceSrc,
  faceLoading,
  faceCurrentMs,
  faceOffsetMs,
  noData,
  series,
  gridMs,
  currentMs,
  durationMs,
  onScrub,
  respondents,
  respondentId,
  onSelectRespondent,
  emotionLabels,
}: {
  open: boolean
  onClose: () => void
  stimulus: EmotionStimulus | null
  stimulusLabel: string
  stageCurrentMs: number
  playing: boolean
  muted: boolean
  onTick: (ms: number) => void
  onEnded: () => void
  onVideoFailed: () => void
  onTogglePlay: () => void
  onToggleMute: () => void
  prototypeReplay: PrototypeReplayData | null
  gazePoint: { x: number; y: number } | null
  /** The same gaze heat map the panel's player draws. */
  heatField?: HeatField | null
  faceSrc: string | null
  /** The respondent's data/recording is still being fetched. */
  faceLoading: boolean
  faceCurrentMs: number
  faceOffsetMs: number
  /** This respondent has no analysed recording for the stimulus. */
  noData: boolean
  series: EmotionSeries | null | undefined
  gridMs: number[]
  currentMs: number
  durationMs: number
  onScrub: (ms: number) => void
  respondents: EmotionRespondent[]
  respondentId: number | null
  onSelectRespondent: (id: number) => void
  emotionLabels: Record<EmotionKey, string>
}) {
  const theme = useTheme()
  const c = theme.affexy.colors
  const { t, i18n } = useTranslation('emotions')
  const rel = useMemo(() => createRelativeFormatter(i18n.language), [i18n.language])
  const phone = useMediaQuery(theme.breakpoints.down('md'))

  // The face recording's own shape (portrait on phones, 4:3/16:9 on laptops),
  // reported by FaceStage once metadata loads.
  const [faceAspect, setFaceAspect] = useState(4 / 3)
  useEffect(() => setFaceAspect(4 / 3), [faceSrc])

  const vec = series && gridMs.length ? vectorAt(series, gridMs, currentMs) : null
  const shares = noData ? null : radarShares(vec)
  const neutralPct = vec ? Math.round(Math.max(0, Math.min(1, vec.neutral)) * 100) : null

  const selectable = useMemo(() => respondents.filter((r) => r.has_data), [respondents])
  const idx = selectable.findIndex((r) => r.id === respondentId)
  const active = respondents.find((r) => r.id === respondentId) ?? null
  const when = (r: EmotionRespondent) => (r.captured_at ? rel(r.captured_at) : r.label)

  const step = useCallback(
    (dir: 1 | -1) => {
      if (!selectable.length) return
      const next = selectable[(Math.max(idx, 0) + dir + selectable.length) % selectable.length]
      onSelectRespondent(next.id)
    },
    [selectable, idx, onSelectRespondent],
  )

  // Keyboard: arrows switch respondent, space plays/pauses (Esc is the Dialog's).
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement) return
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault()
        step(1)
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault()
        step(-1)
      } else if (e.key === ' ') {
        e.preventDefault()
        onTogglePlay()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, step, onTogglePlay])

  // Keep the picked respondent in view in the rail / strip.
  const itemRefs = useRef(new Map<number, HTMLElement>())
  useEffect(() => {
    if (!open || respondentId == null) return
    const el = itemRefs.current.get(respondentId)
    el?.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' })
  }, [open, respondentId])

  const stage = (
    <StimulusStage
      fill
      stimulus={stimulus}
      prototypeReplay={prototypeReplay}
      currentMs={stageCurrentMs}
      playing={playing}
      muted={muted}
      label={stimulusLabel}
      onTick={onTick}
      onEnded={onEnded}
      onVideoFailed={onVideoFailed}
      onTogglePlay={onTogglePlay}
      overlay={
        gazePoint || heatField ? (
          <>
            {heatField && <GazeHeatLayer field={heatField} />}
            {gazePoint && <GazeDot x={gazePoint.x} y={gazePoint.y} />}
          </>
        ) : undefined
      }
    />
  )

  const face = (
    <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
      {faceSrc && !faceLoading ? (
        <FitBox aspect={faceAspect}>
          <FaceStage
            src={faceSrc}
            currentMs={faceCurrentMs}
            offsetMs={faceOffsetMs}
            playing={playing}
            series={series}
            gridMs={gridMs}
            // On a phone the face tile is too small for the emotion chip, and
            // the radar beside it already says the same.
            labels={phone ? undefined : emotionLabels}
            frameWidth="100%"
            frameAspect={faceAspect}
            initialAspect={faceAspect}
            onAspect={setFaceAspect}
          />
        </FitBox>
      ) : (
        <Box sx={{ width: '100%', height: '100%', borderRadius: 2, bgcolor: STAGE_BG, display: 'grid', placeItems: 'center', px: 2 }}>
          {faceLoading ? (
            <CircularProgress size={26} sx={{ color: 'rgba(255,255,255,.8)' }} />
          ) : (
            <Typography sx={{ color: 'rgba(255,255,255,.7)', fontSize: 13, textAlign: 'center' }}>
              {t('noRespondentData')}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  )

  const radar = <EmotionRadar shares={shares} labels={emotionLabels} size={phone ? 96 : 210} showValues={!phone} compact={phone} />

  const neutralChip = neutralPct != null && !noData && (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        px: 1.1,
        py: 0.4,
        borderRadius: 99,
        bgcolor: 'rgba(15,16,36,.62)',
        backdropFilter: 'blur(8px)',
        whiteSpace: 'nowrap',
      }}
    >
      <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: FA_COLOR.neutral }} />
      <Typography sx={{ color: '#fff', fontSize: 11.5, fontWeight: 700 }}>
        {t('fullscreen.neutral', { value: neutralPct })}
      </Typography>
    </Box>
  )

  const respondentItems = respondents.map((r) => {
    const on = r.id === respondentId
    return (
      <Box
        key={r.id}
        ref={(el: HTMLElement | null) => {
          if (el) itemRefs.current.set(r.id, el)
          else itemRefs.current.delete(r.id)
        }}
        role="button"
        aria-pressed={on}
        aria-disabled={!r.has_data}
        title={r.has_data ? when(r) : `${when(r)} · ${t('respondents.noData')}`}
        onClick={() => r.has_data && !on && onSelectRespondent(r.id)}
        sx={{
          flex: '0 0 auto',
          width: phone ? 72 : '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0.5,
          py: 0.5,
          cursor: r.has_data ? 'pointer' : 'default',
          opacity: r.has_data ? 1 : 0.4,
          scrollSnapAlign: 'center',
          '&:hover .fs-av': r.has_data && !on ? { borderColor: 'rgba(255,255,255,.6)' } : {},
        }}
      >
        <Avatar
          className="fs-av"
          src={r.thumbnail_url || undefined}
          sx={{
            width: phone ? 48 : 52,
            height: phone ? 48 : 52,
            bgcolor: 'rgba(255,255,255,.1)',
            color: 'rgba(255,255,255,.7)',
            border: `2px solid ${on ? c.coral : 'transparent'}`,
            boxShadow: on ? '0 0 0 3px rgba(234,80,72,.3)' : 'none',
            transition: 'border-color .12s',
          }}
        >
          <PersonOutlineRoundedIcon />
        </Avatar>
        <Typography
          sx={{
            color: on ? '#fff' : 'rgba(255,255,255,.6)',
            fontSize: phone ? 10 : 10.5,
            fontWeight: on ? 800 : 600,
            maxWidth: '100%',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            textAlign: 'center',
            px: 0.25,
          }}
        >
          {when(r)}
        </Typography>
      </Box>
    )
  })

  const header = (
    <Stack direction="row" alignItems="center" spacing={1.25} sx={{ px: { xs: 1.25, md: 2.5 }, height: { xs: 60, md: 68 }, flex: 'none' }}>
      <IconButton onClick={onClose} aria-label={t('fullscreen.back')} sx={{ color: '#fff', ...GLASS }}>
        <ArrowBackRoundedIcon />
      </IconButton>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: { xs: 15, md: 17 }, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {active ? when(active) : t('individual')}
          {active && Object.values(active.demographics).length > 0 && (
            <Box component="span" sx={{ fontWeight: 600, opacity: 0.6, ml: 1, fontSize: 13 }}>
              {Object.values(active.demographics).join(' · ')}
            </Box>
          )}
        </Typography>
        <Typography sx={{ color: 'rgba(255,255,255,.6)', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {stimulusLabel}
          {idx >= 0 && ` · ${t('fullscreen.position', { index: idx + 1, total: selectable.length })}`}
        </Typography>
      </Box>
      {!phone && (
        <IconButton onClick={onClose} aria-label={t('fullscreen.close')} sx={{ color: '#fff', ...GLASS }}>
          <CloseRoundedIcon />
        </IconButton>
      )}
    </Stack>
  )

  const transport = (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.5}
      sx={{ ...GLASS, borderRadius: 4, px: 1.5, py: 0.75, mx: { xs: 1, md: 2.5 }, mb: { xs: 1.25, md: 2 }, flex: 'none' }}
    >
      <IconButton
        onClick={onTogglePlay}
        aria-label={playing ? 'Pauza' : 'Odtwórz'}
        sx={{ bgcolor: '#fff', color: NAVY, width: 40, height: 40, '&:hover': { bgcolor: 'rgba(255,255,255,.85)' } }}
      >
        {playing ? <PauseRoundedIcon /> : <PlayArrowRoundedIcon />}
      </IconButton>
      <Typography sx={{ color: '#fff', fontVariantNumeric: 'tabular-nums', fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap' }}>
        {fmtTime(currentMs / 1000)} / {fmtTime(durationMs / 1000)}
      </Typography>
      <Slider
        size="small"
        value={Math.min(currentMs, durationMs)}
        max={Math.max(durationMs, 1)}
        onChange={(_, v) => onScrub(v as number)}
        aria-label={t('fullscreen.seek')}
        sx={{
          flex: 1,
          color: c.coral,
          '& .MuiSlider-rail': { bgcolor: 'rgba(255,255,255,.3)' },
          '& .MuiSlider-thumb': { width: 14, height: 14, bgcolor: '#fff' },
        }}
      />
      <IconButton onClick={onToggleMute} aria-label={muted ? t('unmute') : t('mute')} sx={{ color: '#fff' }}>
        {muted ? <VolumeOffRoundedIcon /> : <VolumeUpRoundedIcon />}
      </IconButton>
    </Stack>
  )

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { bgcolor: NAVY, backgroundImage: 'none', color: '#fff', overflow: 'hidden' } }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
        {header}

        {phone ? (
          <>
            {/* Stimulus spans the width; the 16:9 frame takes what it needs. */}
            <Box sx={{ flex: 'none' }}>{stage}</Box>
            <Box sx={{ flex: 1, minHeight: 0, display: 'flex', gap: 1, px: 1, pt: 1.5, pb: 1 }}>
              <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                {radar}
                {neutralChip}
              </Box>
              <Box sx={{ flex: '0 0 38%', minHeight: 0 }}>{face}</Box>
            </Box>
            <Box
              sx={{
                flex: 'none',
                display: 'flex',
                gap: 0.5,
                px: 1,
                pb: 1,
                overflowX: 'auto',
                scrollSnapType: 'x proximity',
                scrollbarWidth: 'none',
                '&::-webkit-scrollbar': { display: 'none' },
              }}
            >
              {respondentItems}
            </Box>
          </>
        ) : (
          <Box sx={{ flex: 1, minHeight: 0, display: 'flex', gap: 2, px: 2.5, pb: 2 }}>
            <Box sx={{ flex: '0 0 36%', minWidth: 0 }}>{face}</Box>
            <Box sx={{ flex: 1, minWidth: 0, position: 'relative' }}>
              <FitBox aspect={STAGE_ASPECT}>{stage}</FitBox>
              <Box sx={{ position: 'absolute', right: 48, bottom: 0, zIndex: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', pointerEvents: 'none' }}>
                {radar}
                <Box sx={{ mt: -3 }}>{neutralChip}</Box>
              </Box>
            </Box>
            <Box
              sx={{
                flex: '0 0 92px',
                ...GLASS,
                borderRadius: 4,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
              }}
            >
              <Typography sx={{ color: 'rgba(255,255,255,.55)', fontSize: 10, fontWeight: 800, letterSpacing: '.06em', textAlign: 'center', pt: 1.5, pb: 0.5 }}>
                {t('fullscreen.respondents').toUpperCase()}
              </Typography>
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflowY: 'auto',
                  px: 0.75,
                  pb: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                  scrollbarWidth: 'thin',
                  scrollbarColor: 'rgba(255,255,255,.25) transparent',
                }}
              >
                {respondentItems}
              </Box>
            </Box>
          </Box>
        )}

        {transport}
      </Box>
    </Dialog>
  )
}
